/**
 * Browser auth for the Cognito Hosted UI (F-009, R7). Authorization-code + PKCE
 * against the Hosted UI, which surfaces BOTH the Okta button and the single
 * break-glass native form. Library-free (Web Crypto only — no Amplify).
 *
 * The SPA sends the ID token as the bearer (api-client). Sign-out ends this app
 * + the brokered Cognito session only (Hosted-UI /logout) — never a global Okta
 * logout (FR-022).
 */
import {
  type Role,
  deriveUserId,
  isAdmin,
  isRole,
  maxRoleRank,
  roleRank,
} from '@suvoda-uat/domain';

export const AUTH_SESSION_STORAGE_KEY = 'suvoda-uat.auth';
const PKCE_VERIFIER_KEY = 'suvoda-uat.pkce.verifier';
const PKCE_STATE_KEY = 'suvoda-uat.pkce.state';

export type AuthSession = {
  readonly idToken: string;
  readonly accessToken: string;
  readonly refreshToken: string;
  readonly expiresAtEpochMs: number;
};

const optionalEnv = (name: string): string | undefined => {
  const value = import.meta.env[name];
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
};

type CognitoAuthConfig = {
  readonly domain: string;
  readonly userPoolClientId: string;
};

const cognitoEnv = (): {
  readonly domain: string | undefined;
  readonly userPoolClientId: string | undefined;
} => ({
  domain: optionalEnv('VITE_COGNITO_DOMAIN'),
  userPoolClientId: optionalEnv('VITE_COGNITO_USER_POOL_CLIENT_ID'),
});

const loadCognitoAuthConfig = (): CognitoAuthConfig => {
  const env = cognitoEnv();
  if (env.domain === undefined || env.userPoolClientId === undefined) {
    throw new Error(
      'Cognito Hosted UI auth is not fully configured (VITE_COGNITO_DOMAIN + VITE_COGNITO_USER_POOL_CLIENT_ID).',
    );
  }
  return { domain: env.domain.replace(/\/+$/, ''), userPoolClientId: env.userPoolClientId };
};

export const isAuthEnabled = (): boolean => {
  const env = cognitoEnv();
  return env.domain !== undefined || env.userPoolClientId !== undefined;
};

/**
 * Okta-initiated login (OIDC Core §4): the Okta dashboard tile redirects to
 * `/login?iss=<issuer>`. The spec REQUIRES validating `iss` against a known
 * allowlist before acting on it, so an arbitrary link can't silently drive
 * users into a login flow. Fails CLOSED — unset `VITE_OKTA_LOGIN_ISSUER` or a
 * mismatch means no auto-start (the manual sign-in button still works).
 */
export const isTrustedLoginIssuer = (iss: string): boolean => {
  const trusted = optionalEnv('VITE_OKTA_LOGIN_ISSUER');
  if (trusted === undefined) return false;
  const stripTrailingSlash = (value: string): string => value.replace(/\/+$/, '');
  return stripTrailingSlash(iss) === stripTrailingSlash(trusted);
};

// ── Session storage ─────────────────────────────────────────────────────────

const isAuthSession = (value: unknown): value is AuthSession => {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<AuthSession>;
  return (
    typeof candidate.idToken === 'string' &&
    candidate.idToken.length > 0 &&
    typeof candidate.accessToken === 'string' &&
    candidate.accessToken.length > 0 &&
    typeof candidate.refreshToken === 'string' &&
    candidate.refreshToken.length > 0 &&
    typeof candidate.expiresAtEpochMs === 'number' &&
    Number.isFinite(candidate.expiresAtEpochMs)
  );
};

export const clearAuthSession = (): void => {
  sessionStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
};

/** Parse the stored session WITHOUT the expiry check (refresh needs it expired). */
const readStoredSession = (): AuthSession | undefined => {
  const raw = sessionStorage.getItem(AUTH_SESSION_STORAGE_KEY);
  if (raw === null) return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    clearAuthSession();
    return undefined;
  }
  if (!isAuthSession(parsed)) {
    clearAuthSession();
    return undefined;
  }
  return parsed;
};

export const getAuthSession = (): AuthSession | undefined => {
  const session = readStoredSession();
  if (session === undefined) return undefined;
  if (session.expiresAtEpochMs <= Date.now()) {
    clearAuthSession();
    return undefined;
  }
  return session;
};

const storeAuthSession = (session: AuthSession): void => {
  sessionStorage.setItem(AUTH_SESSION_STORAGE_KEY, JSON.stringify(session));
};

// ── PKCE helpers (Web Crypto) ───────────────────────────────────────────────

const base64UrlEncode = (bytes: Uint8Array): string => {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const randomToken = (byteLength: number): string =>
  base64UrlEncode(crypto.getRandomValues(new Uint8Array(byteLength)));

const sha256 = async (input: string): Promise<Uint8Array> => {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
  return new Uint8Array(digest);
};

const callbackRedirectUri = (): string => `${window.location.origin}/callback`;

// ── OAuth flow ──────────────────────────────────────────────────────────────

/**
 * Build the Hosted-UI `/authorize` redirect (PKCE). Stashes the `code_verifier`
 * and `state` in sessionStorage for the `/callback` exchange. The caller
 * performs the navigation (`window.location.assign(...)`).
 */
export const buildAuthorizeRedirect = async (): Promise<string> => {
  const config = loadCognitoAuthConfig();
  const verifier = randomToken(32);
  const state = randomToken(16);
  const challenge = base64UrlEncode(await sha256(verifier));
  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  sessionStorage.setItem(PKCE_STATE_KEY, state);
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.userPoolClientId,
    redirect_uri: callbackRedirectUri(),
    scope: 'openid email profile',
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });
  // Okta-only environments build with VITE_COGNITO_IDENTITY_PROVIDER=Okta —
  // the Hosted UI then forwards straight to the IdP instead of a chooser.
  const identityProvider = optionalEnv('VITE_COGNITO_IDENTITY_PROVIDER');
  if (identityProvider !== undefined) params.set('identity_provider', identityProvider);
  return `${config.domain}/oauth2/authorize?${params.toString()}`;
};

type TokenResponse = {
  readonly id_token?: string;
  readonly access_token?: string;
  readonly refresh_token?: string;
  readonly expires_in?: number;
};

const sessionFromTokenResponse = (
  raw: TokenResponse,
  fallbackRefreshToken?: string,
): AuthSession => {
  const idToken = raw.id_token;
  const accessToken = raw.access_token;
  const refreshToken = raw.refresh_token ?? fallbackRefreshToken;
  const expiresIn = raw.expires_in;
  if (
    idToken === undefined ||
    accessToken === undefined ||
    refreshToken === undefined ||
    expiresIn === undefined
  ) {
    throw new Error('Token endpoint did not return the expected tokens.');
  }
  return { idToken, accessToken, refreshToken, expiresAtEpochMs: Date.now() + expiresIn * 1000 };
};

const postToTokenEndpoint = async (
  config: CognitoAuthConfig,
  params: Record<string, string>,
): Promise<Response> =>
  fetch(`${config.domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams(params).toString(),
  });

/**
 * Exchange the `/callback` authorization code for tokens. Validates `state`
 * against the value stashed at redirect time (CSRF) and uses the stored
 * `code_verifier` (PKCE). Persists + returns the session.
 */
export const exchangeCodeForTokens = async (code: string, state: string): Promise<AuthSession> => {
  const config = loadCognitoAuthConfig();
  const expectedState = sessionStorage.getItem(PKCE_STATE_KEY);
  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
  if (expectedState === null || verifier === null || state !== expectedState) {
    throw new Error('Sign-in could not be verified (state mismatch). Please try again.');
  }
  const response = await postToTokenEndpoint(config, {
    grant_type: 'authorization_code',
    client_id: config.userPoolClientId,
    code,
    redirect_uri: callbackRedirectUri(),
    code_verifier: verifier,
  });
  if (!response.ok) {
    throw new Error('Sign-in failed during token exchange.');
  }
  const session = sessionFromTokenResponse((await response.json()) as TokenResponse);
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(PKCE_STATE_KEY);
  storeAuthSession(session);
  return session;
};

/**
 * Refresh the session using the stored refresh token (the rotated/old token is
 * reused if the endpoint omits a new one). Clears the session on failure.
 */
export const refreshSession = async (): Promise<AuthSession | undefined> => {
  const current = readStoredSession();
  if (current === undefined) return undefined;
  const config = loadCognitoAuthConfig();
  const response = await postToTokenEndpoint(config, {
    grant_type: 'refresh_token',
    client_id: config.userPoolClientId,
    refresh_token: current.refreshToken,
  });
  if (!response.ok) {
    clearAuthSession();
    return undefined;
  }
  const session = sessionFromTokenResponse(
    (await response.json()) as TokenResponse,
    current.refreshToken,
  );
  storeAuthSession(session);
  return session;
};

/** The Hosted-UI `/logout` URL (this-app + Cognito session only, FR-022). */
export const buildLogoutUrl = (): string | undefined => {
  const env = cognitoEnv();
  if (env.domain === undefined || env.userPoolClientId === undefined) return undefined;
  const params = new URLSearchParams({
    client_id: env.userPoolClientId,
    logout_uri: `${window.location.origin}/login`,
  });
  return `${env.domain.replace(/\/+$/, '')}/logout?${params.toString()}`;
};

/**
 * Sign out: clear the local session and redirect to the Hosted-UI `/logout`
 * (ends the brokered Cognito session, returns to `/login`). NOT a global Okta
 * logout (FR-022). If auth is unconfigured, only the local session is cleared.
 */
export const signOut = (): void => {
  clearAuthSession();
  const url = buildLogoutUrl();
  if (url !== undefined) window.location.assign(url);
};

// ── Role-aware UI gating (FR-017) ────────────────────────────────────────────
// Defense in depth ONLY — the server's authorization-policy is authoritative
// (R4/I9). These mirror the policy thresholds to hide actions the role can't do.

const decodeJwtPayload = (token: string): Record<string, unknown> | undefined => {
  const parts = token.split('.');
  const payloadPart = parts[1];
  if (parts.length !== 3 || payloadPart === undefined) return undefined;
  try {
    const json = atob(payloadPart.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return undefined;
  }
};

/** The current Principal's roles, decoded from the ID token's `custom:roles`. */
export const currentRoles = (): readonly Role[] => {
  const session = getAuthSession();
  if (session === undefined) return [];
  const raw = decodeJwtPayload(session.idToken)?.['custom:roles'];
  return typeof raw === 'string' ? raw.split(/\s+/).filter(isRole) : [];
};

const FALLBACK_USER_NAME = 'Account';

const claimString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;

/**
 * The current Principal's stable user id, derived from the SAME ID-token claims
 * (and the shared `deriveUserId` normalisation) the API's actor middleware
 * uses — so `Trial.owner.id` comparisons ("My Projects") agree with the server.
 */
export const currentUserId = (): string | undefined => {
  const session = getAuthSession();
  if (session === undefined) return undefined;
  const claims = decodeJwtPayload(session.idToken);
  if (claims === undefined) return undefined;
  return deriveUserId([
    claimString(claims['sub']),
    claimString(claims['cognito:username']),
    claimString(claims['email']),
  ]);
};

/**
 * The signed-in user's display name for the top bar, from the ID token's OIDC
 * `profile` claims (the `openid email profile` scope). Falls back through
 * `name` → `given_name family_name` → `email` → a safe default, so it is always
 * a non-empty string even with no/expired session.
 */
export const currentUserName = (): string => {
  const session = getAuthSession();
  if (session === undefined) return FALLBACK_USER_NAME;
  const claims = decodeJwtPayload(session.idToken);
  if (claims === undefined) return FALLBACK_USER_NAME;
  const name = claimString(claims['name']);
  if (name !== undefined) return name;
  const full = [claimString(claims['given_name']), claimString(claims['family_name'])]
    .filter((part): part is string => part !== undefined)
    .join(' ');
  if (full.length > 0) return full;
  return claimString(claims['email']) ?? FALLBACK_USER_NAME;
};

/** Editor or higher — may author (create/upload/triage/generate/edit/title). */
export const canAuthor = (roles: readonly Role[] = currentRoles()): boolean =>
  maxRoleRank(roles) >= roleRank('editor');

/** Approver or higher — may finalise (sign-off). */
export const canApprove = (roles: readonly Role[] = currentRoles()): boolean =>
  maxRoleRank(roles) >= roleRank('approver');

/** Admin only — may open the Team directory (feature 015; server 403s too). */
export const canManageTeam = (roles: readonly Role[] = currentRoles()): boolean => isAdmin(roles);

/** Has at least one mapped role — i.e. NOT the fail-closed "request access" state. */
export const hasAccess = (roles: readonly Role[] = currentRoles()): boolean => roles.length > 0;
